/**
 * Lightweight scale utilities — replaces Vega/D3 scale functions.
 */

/**
 * Linear scale: maps a numeric domain to a pixel range.
 */
export function linearScale(
  domain: [number, number],
  range: [number, number]
): (value: number) => number {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0 || 1;
  return (value: number) => r0 + ((value - d0) / span) * (r1 - r0);
}

/**
 * Band scale: maps discrete categories to equal-width bands within a range.
 * Returns the start position of each band.
 */
export function bandScale(categories: string[], range: [number, number], padding = 0.1) {
  const [r0, r1] = range;
  const totalRange = r1 - r0;
  const n = categories.length || 1;
  const step = totalRange / (n + padding * 2);
  const bandwidth = step * (1 - padding);
  const offset = step * padding;

  const lookup = new Map<string, number>();
  categories.forEach((cat, i) => {
    lookup.set(cat, r0 + offset + i * step);
  });

  const scale = (category: string) => lookup.get(category) ?? r0;
  scale.bandwidth = () => bandwidth;
  scale.step = () => step;

  return scale;
}

/**
 * Returns ticks enclosing a finite ascending domain; equal bounds return only that value.
 * @param min - Inclusive lower bound in data units.
 * @param max - Inclusive upper bound in data units.
 * @param targetCount - Approximate positive finite tick count.
 * @param minimumStep - Smallest positive finite interval in data units; defaults to 1 for counts.
 * @returns Ordered tick values spanning the domain.
 */
export function niceAxisTicks(
  min: number,
  max: number,
  targetCount = 5,
  minimumStep = 1
): number[] {
  if (min === max) return [min];

  const range = max - min;
  const roughStep = range / targetCount;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const residual = roughStep / magnitude;

  let niceStep: number;
  if (residual <= 1.5) niceStep = magnitude;
  else if (residual <= 3) niceStep = 2 * magnitude;
  else if (residual <= 7) niceStep = 5 * magnitude;
  else niceStep = 10 * magnitude;

  niceStep = Math.max(niceStep, minimumStep);

  const niceMin = Math.floor(min / niceStep) * niceStep;
  const niceMax = Math.ceil(max / niceStep) * niceStep;

  const ticks: number[] = [];
  for (let v = niceMin; v <= niceMax + niceStep * 0.5; v += niceStep) {
    ticks.push(Math.round(v * 1e10) / 1e10); // avoid floating-point noise
  }
  return ticks;
}
